import type { LucideIcon } from "lucide-react";
import { Activity, Shield, User } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import {
  hairlineClass,
  segmentedItemActiveClass,
  segmentedItemClass,
  segmentedTrackClass,
  sidebarRowActiveClass,
  sidebarRowClass,
} from "@/lib/card-styles";
import { typeMetaClass } from "@/lib/typography";

export const PROFILE_SECTIONS = ["profile", "account", "statistics"] as const;
export type ProfileSection = (typeof PROFILE_SECTIONS)[number];

interface SectionDef {
  id: ProfileSection;
  labelKey: string;
  fallback: string;
  icon: LucideIcon;
}

/**
 * The modal's three panes, in one list.
 *
 * Declared once so the desktop rail and the mobile tabs cannot disagree about
 * order, labels, or icons — they used to, and each had its own hardcoded copy
 * (the mobile tabs were already relabelled "Stats" for width).
 */
const SECTIONS: SectionDef[] = [
  { id: "profile", labelKey: "settings.profile.title", fallback: "Profile", icon: User },
  { id: "account", labelKey: "settings.accountPanel.title", fallback: "Account", icon: Shield },
  {
    id: "statistics",
    labelKey: "settings.profile.statistics",
    fallback: "Statistics",
    icon: Activity,
  },
];

export interface ProfileSectionNavProps {
  active: ProfileSection;
  onSelect: (section: ProfileSection) => void;
  /** `rail` is the desktop sidebar column; `tabs` is the mobile strip. */
  variant: "rail" | "tabs";
  /** Trailing status per section — e.g. a "blocked" marker on notifications. */
  badges?: Partial<Record<ProfileSection, React.ReactNode>>;
  className?: string;
}

/**
 * Navigation between the profile modal's three panes.
 *
 * ## Two layouts, one list
 *
 * The desktop rail reuses `sidebarRowClass` / `sidebarRowActiveClass` so it is
 * literally the same row the global nav draws. The previous version had its own
 * `border-l-2` + `bg-primary/5` treatment, which is a near-copy of the rail's
 * active state with a different accent bar — two definitions of "the row you
 * are on" in one dialog.
 *
 * The mobile strip uses `segmentedTrackClass` instead: a rail's affordance is
 * "a place in a column", and a horizontal strip of three has no column, so the
 * selected segment is a lifted paper chip.
 */
export function ProfileSectionNav({
  active,
  onSelect,
  variant,
  badges,
  className,
}: ProfileSectionNavProps) {
  const { t } = useTranslation();

  const labels = SECTIONS.map((section) => ({
    ...section,
    label: t(section.labelKey, section.fallback),
  }));

  if (variant === "tabs") {
    return (
      <div className={cn("shrink-0 border-b px-4 py-2.5", hairlineClass, className)}>
        {/* `aria-pressed` rather than `role="tab"`: the pane behind this strip
            is not a tabpanel, and a tablist with no tabpanel is a worse
            promise than a plain toggle group. This also matches the theme
            switcher and the streak modal's switcher exactly. */}
        <div role="group" aria-label={t("settings.profile.sections", "Profile sections")} className={cn(segmentedTrackClass, "w-full")}>
          {labels.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              aria-pressed={active === id}
              onClick={() => onSelect(id)}
              className={cn(
                segmentedItemClass,
                "py-2",
                active === id && segmentedItemActiveClass,
              )}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate">{label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <nav
      aria-label={t("settings.profile.sections", "Profile sections")}
      className={cn(
        "space-y-0.5 p-3",
        // A barely-tinted rail reads as "unselected" for the active row too,
        // because the page behind it is white. The rail carries its own warm
        // wash so the blue active tile has something to sit on.
        "bg-stone-50/70 dark:bg-stone-800/25",
        className,
      )}
    >
      <p className={cn("px-2 pb-1.5 pt-1", typeMetaClass)}>
        {t("settings.profile.sections", "Settings")}
      </p>
      {labels.map(({ id, label, icon: Icon }) => {
        const isActive = active === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onSelect(id)}
            aria-current={isActive ? "page" : undefined}
            className={cn(
              sidebarRowClass,
              "relative !py-2.5 pl-3 pr-3 text-[13px]",
              isActive
                ? cn(sidebarRowActiveClass, "font-semibold shadow-sm ring-1 ring-primary/25")
                : "text-stone-600 hover:bg-stone-200/60 dark:text-stone-400 dark:hover:bg-stone-700/50",
            )}
          >
            <Icon
              className={cn(
                "h-[18px] w-[18px] shrink-0 transition-colors",
                isActive ? "text-primary" : "text-stone-500 dark:text-stone-400",
              )}
              aria-hidden="true"
            />
            <span className="truncate">{label}</span>
            {badges?.[id]}
          </button>
        );
      })}
    </nav>
  );
}

export default ProfileSectionNav;